import { MutableRefObject, useRef } from "react";
import { UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { useTimerVolumeContext } from "@contexts/TimerVolumeContext";
import { firebase } from "@firebase-local/firebase";
import { PersonalPomodoroService } from "../../../service/PersonalPomodoroService";
import { PomodoroAnalyticsService } from "../../../service/PomodoroAnalyticsService";
import Pomodoro from "./Pomodoro";
import { PAUSE_TIMER_LABEL } from "./TimerWidgetConstants";
import { useAlarmSoundOptions } from "./hooks/useAlarmSoundOptions";
import { useLocalPomodoro } from "./hooks/useLocalPomodoro";

interface PersonalPomodoroProps {
    hidden: boolean;
    updateCompletedPomos: (numComplete: number) => void;
    updateHidePomodoroCount: (value: boolean) => void;
    user: firebase.User;
    userData: UserEntity;
}
export default function PersonalPomodoro(props: PersonalPomodoroProps) {
    // Note: if the timerVolume (in saved user data) is set to 0, timerAudioVol will default to DEFAULT_TIMER_VOLUME
    const timerAudioRef: MutableRefObject<HTMLAudioElement | null> = useRef(null);

    const { timerVolume, changeTimerVolume } = useTimerVolumeContext();

    const amplitudeService = AmplitudeService.getInstance();
    const analyticsService = PomodoroAnalyticsService(amplitudeService);
    const pomodoroService = PersonalPomodoroService(props.user, props.userData, analyticsService);
    const alarmSoundOptions = useAlarmSoundOptions(pomodoroService);
    const {
        selectPomodoro,
        selectShortBreak,
        selectLongBreak,
        saveSettings,
        start,
        pause,
        reset,
        isRunning,
        timerDisplayText,
        pomodoroSettingsInput,
        dispatchPomodoroSettingsInput,
    } = useLocalPomodoro({
        hidden: props.hidden,
        pomodoroService,
        alarmSoundOptions,
        onPomodoroCountChange: props.updateCompletedPomos,
        onHidePomodoroCountChange: props.updateHidePomodoroCount,
        audioRef: timerAudioRef,
    });

    const toggleTimer = () => {
        if (isRunning) {
            pause();
        } else {
            start(true);
        }
    };

    return (
        <Pomodoro
            hidden={props.hidden}
            stopTimerActionButtonLabel={PAUSE_TIMER_LABEL}
            canEditSettings={false}
            isRunning={isRunning}
            timerDisplayText={timerDisplayText}
            alarmSoundOptions={alarmSoundOptions}
            pomodoroService={pomodoroService}
            pomodoroSettingsInput={pomodoroSettingsInput}
            dispatchPomodoroSettingsInput={dispatchPomodoroSettingsInput}
            timerAudioRef={timerAudioRef}
            timerVolume={timerVolume}
            setTimerVolume={(newVol: number) => changeTimerVolume("personal", newVol)}
            onTimerToggle={toggleTimer}
            onTimerReset={() => reset()}
            onSelectPomodoro={selectPomodoro}
            onSelectShortBreak={selectShortBreak}
            onSelectLongBreak={selectLongBreak}
            onSettingsSave={saveSettings}
            allowAutotransitionToggle={true}
        />
    );
}
